import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, MapPin, Mail, Phone, Loader2 } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Reveal } from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { name: "name", label: "Nom", type: "text", ph: "Jean Dupont", req: true, half: true },
  { name: "company", label: "Société", type: "text", ph: "Votre entreprise", req: false, half: true },
  { name: "email", label: "Email", type: "email", ph: "jean@entreprise.fr", req: true, half: true },
  { name: "phone", label: "Téléphone", type: "tel", ph: "+33 6 00 00 00 00", req: false, half: true },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Merci de remplir les champs obligatoires.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message envoyé !", { description: "Notre équipe vous recontacte sous 24h." });
      setForm({ name: "", company: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Une erreur est survenue.", { description: "Veuillez réessayer dans un instant." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="[ Contact ]"
        titleLines={["Parlons de votre", "transformation."]}
        intro="Un diagnostic industriel offert, sans engagement. Décrivez votre contexte, nous revenons vers vous sous 24h."
      />

      <section className="bg-paper py-20 md:py-28" data-testid="contact-section">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <Reveal>
              <form onSubmit={submit} className="grid grid-cols-2 gap-5" data-testid="contact-form">
                {FIELDS.map((f) => (
                  <div key={f.name} className={f.half ? "col-span-2 sm:col-span-1" : "col-span-2"}>
                    <label className="label-mono text-ink/60 block mb-2">{f.label}{f.req && <span className="text-gold"> *</span>}</label>
                    <input
                      type={f.type}
                      value={form[f.name]}
                      onChange={update(f.name)}
                      placeholder={f.ph}
                      data-testid={`contact-${f.name}`}
                      className="w-full bg-white border hairline rounded-sm px-4 py-3.5 outline-none focus:border-gold transition-colors"
                    />
                  </div>
                ))}
                <div className="col-span-2">
                  <label className="label-mono text-ink/60 block mb-2">Message<span className="text-gold"> *</span></label>
                  <textarea
                    rows={6}
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Décrivez votre problématique industrielle..."
                    data-testid="contact-message"
                    className="w-full bg-white border hairline rounded-sm px-4 py-3.5 outline-none focus:border-gold transition-colors resize-none"
                  />
                </div>
                <div className="col-span-2">
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit"
                    className="group inline-flex items-center gap-3 rounded-full bg-ink text-paper px-8 py-4 label-mono hover:bg-gold hover:text-ink transition-colors duration-300 disabled:opacity-60"
                  >
                    {loading ? <><Loader2 size={16} className="animate-spin" /> Envoi...</> : <>Envoyer ma demande <Send size={15} className="group-hover:translate-x-1 transition-transform" /></>}
                  </button>
                </div>
              </form>
            </Reveal>
          </div>

          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="rounded-sm bg-ink-900 grain text-paper p-8 relative overflow-hidden">
                <div className="relative z-10 space-y-8">
                  {[
                    { icon: MapPin, l: "Adresse", v: "Paris, France" },
                    { icon: Mail, l: "Email", v: "novalean26@outlook.fr" },
                    { icon: Phone, l: "Téléphone", v: "+33 6 68 75 14 16" },
                  ].map((c) => (
                    <div key={c.l} className="flex gap-4">
                      <div className="w-11 h-11 rounded-sm bg-gold/15 flex items-center justify-center shrink-0">
                        <c.icon size={18} className="text-gold" />
                      </div>
                      <div>
                        <div className="label-mono text-steel">{c.l}</div>
                        <div className="mt-1 text-paper/90">{c.v}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-sm overflow-hidden border hairline mt-5" data-testid="contact-map">
                <iframe
                  title="Nova Lean Consulting - localisation"
                  src="https://www.openstreetmap.org/export/embed.html?bbox=4.8543%2C45.7597%2C4.9043%2C45.7797&layer=mapnik&marker=45.7697%2C4.8793"
                  className="w-full h-72 grayscale contrast-125"
                  loading="lazy"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
